import { Box, BoxProps, styled } from '@mui/material'
import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import Navigator, { TNavigatorItem } from '@/app/component/atom/navigator'
import Loading from '@/app/component/guard/loading'
import SidebarLayout from '@/app/component/layout/sidebar-layout'
import { cannot } from '@/app/helpers'
import { selectUserRole } from '@/app/module/user/store/selectors'
import { selectUserError, selectUserInit, selectUserLoading } from '@/app/module/user/store/ts/selectors'

import ApiKeysContainer from './api-keys.container'
import ProfileInfoForm from './profile-info-form'
import ResetPasswordSection from './reset-password'
import TwoFa from './two-fa'

export enum AccountTab {
  Profile = 'profile',
  ResetPassword = 'reset-password',
  TwoFactor = '2fa',
  ApiKeys = 'api-tokens',
}

const navigatorItems: TNavigatorItem<AccountTab>[] = [
  {
    icon: 'account_circle',
    label: 'Profile',
    path: AccountTab.Profile,
  },
  {
    icon: 'lock_reset',
    label: 'Password Reset',
    path: AccountTab.ResetPassword,
  },
  {
    icon: 'security',
    label: '2FA Authentication',
    path: AccountTab.TwoFactor,
  },
  {
    icon: 'vpn_key',
    label: 'API Tokens',
    path: AccountTab.ApiKeys,
  },
]

type Props = {
  tabName: AccountTab
}

const Account: React.FC<Props> = ({ tabName }) => {
  const navigate = useNavigate()
  const error = useSelector(selectUserError)
  const userInited = useSelector(selectUserInit)
  const userLoading = useSelector(selectUserLoading)
  const role = useSelector(selectUserRole)

  const renderTabContent = () => {
    switch (tabName) {
      case AccountTab.Profile:
        return <ProfileInfoForm />
      case AccountTab.ResetPassword:
        return <ResetPasswordSection />
      case AccountTab.TwoFactor:
        return <TwoFa />
      case AccountTab.ApiKeys: {
        if (role === 'cati_agent') {
          return null
        }
        return <ApiKeysContainer />
      }
      default:
        return cannot(tabName)
    }
  }

  const filteredNavigatorItems = React.useMemo(() => {
    if (role === 'cati_agent') {
      return navigatorItems.filter((item) => item.path !== AccountTab.ApiKeys)
    }
    return navigatorItems
  }, [role])

  const handleTabChange = React.useCallback(
    (tab: AccountTab) => {
      navigate(`/account/${tab}`)
    },
    [navigate],
  )

  const sidebar = (
    <Navigator
      activePath={tabName}
      id="profile"
      items={filteredNavigatorItems}
      title="Account"
      onItemClick={handleTabChange}
    />
  )

  const content = (
    <Container id={`profile-content-${tabName}`} tabName={tabName}>
      {renderTabContent()}
    </Container>
  )

  const loading = !userInited || userLoading

  return (
    <Loading isLoading={loading} error={error as any}>
      <SidebarLayout sidebar={sidebar} content={content} />
    </Loading>
  )
}

const scrollingTabs: AccountTab[] = [AccountTab.ApiKeys]

const Container = styled(Box, { shouldForwardProp: (prop) => prop !== 'tabName' })<BoxProps & { tabName: AccountTab }>(
  ({ tabName }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    ...(scrollingTabs.includes(tabName) && {
      height: '100%',
      width: '100%',
    }),
  }),
)

export default Account
